/**
 * Turning the Twilio diagnostic into the four lines the card shows.
 *
 * Pure and its own file for the same reason `bindingNotes` is: the interesting
 * part is not the rendering, it is deciding how loudly to say each thing. The
 * three server states do not map one-to-one onto three tones, and the place that
 * decision is made should be testable without mounting a card.
 *
 * The rule that matters: `unknown` is never red. A Twilio that did not answer
 * has to read as "could not check", because a checklist that turns an outage
 * into "your caller id is gone" sends somebody to change a setting that was
 * never wrong - which is a worse failure than the single green tick this
 * replaces.
 *
 * The server's half is `apps/api/src/modules/crm-calls/lib/diagnostics.ts`.
 */
import type { NoteTone } from "../panel/fields";
import type { DiagRow, TelephonyDiagnostics } from "../../../../lib/calls";

/** Which of the four rows this is. Doubles as the i18n key for its label. */
export type ChecklistRowId = "keyPair" | "region" | "callerId" | "account";

export const CHECKLIST_ROWS: readonly ChecklistRowId[] = ["keyPair", "region", "callerId", "account"];

export interface ChecklistLine {
  id: ChecklistRowId;
  tone: NoteTone;
  /** i18n key under `settings.telephony.check`, e.g. `keyPair.key_not_in_region`. */
  key: string;
  /** The one value worth showing beside the sentence, when there is one. */
  detail: string;
}

/**
 * `not_configured` is left out of every row on purpose.
 *
 * It is what the whole diagnostic returns when the Twilio credentials are
 * missing, and the card already says that in its own sentence. Four more lines
 * repeating it would make one problem look like five.
 */
function toneOf(row: DiagRow): NoteTone {
  if (row.state === "ok") return "ok";
  if (row.state === "bad") return "bad";
  // Everything unknown is a remark. `not_set` is the deliberately quiet one: an
  // installation with no caller id chosen yet has not made a mistake.
  return row.reason === "not_set" ? "mut" : "warn";
}

/**
 * The four lines, in the order the failures actually happen.
 *
 * Key pair and region first because they are what stops the browser signing in
 * at all; caller id and account next because they stop individual calls. An
 * installation with nothing configured gets an empty list, not four grey rows.
 */
export function telephonyChecklist(diagnostics: TelephonyDiagnostics | undefined): ChecklistLine[] {
  if (!diagnostics) return [];
  const lines: ChecklistLine[] = [];
  for (const id of CHECKLIST_ROWS) {
    const row = diagnostics[id];
    if (!row) continue;
    if (row.reason === "not_configured") continue;
    lines.push({
      id,
      tone: toneOf(row),
      // The state alone for a pass, the reason for everything else: "ok" needs
      // one sentence per row, while a failure needs to name which failure it is.
      key: `${id}.${row.state === "ok" ? "ok" : row.reason ?? "unknown"}`,
      detail: row.detail ?? "",
    });
  }
  return lines;
}

/** True when at least one row is red - what the card's own summary hangs off. */
export function checklistHasFailure(lines: readonly ChecklistLine[]): boolean {
  return lines.some((l) => l.tone === "bad");
}
